import { AbstractControl } from './AbstractControl';
import { FormControl } from './FormControl';

/**
 * Represent a logical grouping of different form control types. This control type is used
 * to build your form model in a more convenient way because it aggregates all of the provided
 * form controls' value, as well as keeps track of your form's overall validity state.
 * Example usage:
 *
 * ```
 *  export function AccountForm(props: Props) {
 *    const accountFormGroupRef = useRef(
 *      new FormGroup<AccountDto>({
 *        firstName: new FormControl('', [Validators.checkNotEmpty]),
 *        lastName: new FormControl('', [Validators.checkNotEmpty]),
 *        email: new FormControl('', [Validators.checkValidEmail])
 *      })
 *    );
 *
 *    const [isFormValid, setIsFormValid] = useState(accountFormGroupRef.isValid());
 *
 *    const onSubmit() = () => {
 *      // `accountFormGroupRef.getValue()` will aggregate all form controls'value passed
 *      // to it from above and create a new form value object that looks like this
 *      // {
 *      //    "firstName": "...",
 *      //    "lastName": "...",
 *      //    "email": "..."
 *      // }
 *      props.onSubmit(accountFormGroupRef.getValue());
 *    };
 *
 *    useEffect(() => {
 *      const subscription = accountFormGroupRef.current.validityChanges()
 *        .subscribe({
 *          next: setIsFormValid
 *        });
 *      return () => {
 *        // Must unsubscribe to prevent memory leaks when this hook runs
 *        subscription.unsubscribe();
 *      };
 *    }, [accountFormGroupRef]); // Have to depend on this to prevent this hook from running on every render
 *
 *    return (
 *      <Form
 *        className='...'
 *        formGroup={accountFormGroupRef}
 *        onSubmit={...}>
 *
 *        <ShortTextFormField
 *          label='...'
 *          formControl={accountFormGroupRef.findControl('firstName')} />
 *
 *        <ShortTextFormField
 *          label='...'
 *          formControl={accountFormGroupRef.findControl('lastName')} />
 *
 *        <ShortTextFormField
 *          label='...'
 *          formControl={accountFormGroupRef.findControl('email')} />
 *
 *        <Button
 *          style='raised'
 *          color='primary'
 *          disabled={!isFormValid}>
 *          Submit
 *        </Button>
 *      </Form>
 *    );
 *  }
 * ```
 */
export class FormGroup<T> extends AbstractControl<T> {

  private readonly _invalidFormControls = new Set<AbstractControl<any>>();
  private readonly _controls = new Map<string, AbstractControl<any>>();

  constructor(controls?: { [K in keyof T]: AbstractControl<T[K]> | T[K] }) {
    super();

    if (controls) {
      for (const [k, v] of Object.entries(controls)) {
        if (v instanceof AbstractControl) {
          this._bindToControl(k, v);
        } else {
          this._controls.set(k, new FormControl(v));
        }
      }
    }
  }

  private _bindToControl(controlName: string, control: AbstractControl<any>) {
    this._controls.set(controlName, control);
    control.validityChanges()
      .subscribe({
        next: () => this._onValidityChanged(control)
      });
    control.valueChanges()
      .subscribe({
        next: (value: any) => this._onControlValueChanged(controlName, value)
      });
    this._onValidityChanged(control);
  }

  private _onValidityChanged(control: AbstractControl<any>) {
    if (control.isValid()) {
      this._invalidFormControls.delete(control);
    } else {
      this._invalidFormControls.add(control);
    }
    this._isValid = this._invalidFormControls.size === 0;
    this._validityChangeNotifier.next(this._isValid);
  }

  private _onControlValueChanged(controlName: string, value: any) {
    // When a child control is reset, its value triggers this callback
    // so we only want to update the _isPristine flag of this FormControlGroup
    // if the child control was not reset to its initial value
    if (!this._controls.get(controlName).isPristine()) {
      this._isPristine = false;
    }
    this._valueChangeNotifier.next({
      [controlName]: value
    } as any);
  }

  /**
   * Aggregate the value of each child form control and return the aggregation as an object
   *
   * @returns The aggregated form value
   */
  getValue() {
    const value = {} as T;
    for (const [controlName, control] of this._controls) {
      (value as any)[controlName] = control.getValue();
    }

    return value;
  }

  /**
   * Partially update the value for this form group
   *
   * @param newValue Partial value for this form group
   */
  setValue<K extends keyof T = keyof T>(newValue: Pick<T, K>) {
    for (const [k, v] of Object.entries(newValue)) {
      if (this._controls.has(k)) {
        this._controls.get(k).setValue(v);
      } else {
        this._controls.set(k, new FormControl(v));
      }
    }
  }

  /**
   * Return a child form control with the given name, TypeScript will automatically shows the list of available
   * form control names to choose from thanks to generics. If a child form control is a form group, then you
   * can chain their names using the dot characters to get to the inner control. For example, `accountFormGroup.findControl('address.zipCode')`
   *
   * @param paths The name of the form control to retrieve
   * @returns
   */
  findControl<K extends keyof T = keyof T, E extends AbstractControl<T[K]> = FormControl<T[K]>>(paths: K & string): E {
    let control = this._controls.get(paths);
    if (!paths.includes('.')) {
      if (control === undefined) {
        throw new Error(`Unable to locate control with name "${paths}"`);
      }
      return control as any;
    }
    const controlPaths = paths.split('.');
    control = this._controls.get(controlPaths.shift());
    for (const controlPath of controlPaths) {
      if (control === undefined) {
        throw new Error(`Unable to locate control with name "${controlPath}" in property path "${paths}"`);
      }
      if (('findControl' in control)) {
        control = (control as any).findControl(controlPath);
      }
    }
    return control as any;
  }

  setControl<K extends keyof T = keyof T>(controlName: K & string, control: AbstractControl<T[K]>) {
    this._bindToControl(controlName, control);
    if (control.isInvalid()) {
      this._validityChangeNotifier.next(false);
    }
  }

  reset() {
    this._isPristine = true;
    this._controls.forEach(control => control.reset());
    this._resetNotifier.next();
  }

  disable() {
    super.disable();
    this._controls.forEach(control => control.disable());
  }

  enable() {
    super.enable();
    this._controls.forEach(control => control.enable());
  }

}
